import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'antd/lib/form/Form';
import { Form, Row, Col, Select, Spin, Input, Typography, Space } from 'antd';
import Modal from '../Modal/Modal';
import styles from './index.module.less';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../redux/selectors/user';
import { RuleObject } from 'rc-field-form/lib/interface';
import { Button } from '../Button/Button';
import Map, { LocationPoint, MapUseCase } from '../Map/Map';
import { DrivingDistanceRoutesTransformed, Location, SearchLocation } from '../../api/models/locations';
import { DUBAI_LAT_LNG } from '../../utils/constants';
import { getLocationSelectOptions } from '../../utils/componentHelpers';
import { getDropOffLocationTitle } from '../../utils/deliveries';
import { AppDispatch } from '../../types/store';
import {
  customDropoffBackClicked,
  customDropoffCancelClicked,
  customDropoffConfirmClicked,
  customDropoffSetLocationOnMapClicked,
  customDropoffSubmitClicked,
  dropOffLocationChanged,
  dropofPinDragged,
  emptyDropOffError,
  nearbyLocationNotFound,
  ringfencingError,
  samePickupDropOffError
} from '../../utils/analytika/events';
import isEqual from 'react-fast-compare';
import { isValidDistanceV2 } from '../../utils/userConfigValidators';
import Overlay from '../Map/Overlay';
import { SearchOutlined } from '@ant-design/icons';
import i18next from '../../i18next';
import InfoBar from '../Map/InfoBar';
import { ANALYTIKA_EVENTS_V2, ANALYTIKA_SCREENS } from '../../utils/analytika/enums';
import { getReverseGeocode } from '../../api/locations';
import classnames from 'classnames';
import { noNearbyLocationFound } from '../../screens/deliveries/messages';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';
import { reverseGeocodingBlocked } from '../../screens/deliveries/messages';

type AddDropoffModalProps = {
  visible: boolean;
  onClose: () => void;
  pickupLocation: Location;
  onSubmitModal: (
    fields: AddDropoffFormValues,
    directions: DrivingDistanceRoutesTransformed | null,
    dropoffLocation: SearchLocation
  ) => void;
  onDropoffSearchStart: (searchString: string) => void;
  isDropoffSearchLoading: boolean;
  dropoffLocationSelectOptions: JSX.Element[]; //TODO remove
  dropOffLocations: SearchLocation[];
  preSelectedDropoffId?: string;
  usersBuildingName?: string;
  initialDropoffLocation?: SearchLocation;
  isEditMode?: boolean;
  analyticsScreen?: ANALYTIKA_SCREENS;
};

export type AddDropoffFormValues = {
  dropOffId: string;
  lat: number;
  lng: number;
  buildingName: string;
};

const overlay = (
  <Overlay customIcon={() => <SearchOutlined />} text={i18next.t('deliveries.mapCaption.addDropOffAddress')} />
);

const AddDropoffModal = ({
  visible,
  onClose,
  onSubmitModal,
  pickupLocation,
  onDropoffSearchStart,
  isDropoffSearchLoading,
  preSelectedDropoffId,
  usersBuildingName,
  dropOffLocations: searchDrofOffLocations,
  initialDropoffLocation,
  isEditMode,
  analyticsScreen = ANALYTIKA_SCREENS.MARKETPLACE_REQUEST_DELIVERY
}: AddDropoffModalProps) => {
  const { t } = useTranslation();
  const [form] = useForm<AddDropoffFormValues>();
  const {
    deliveries: { getDistance },
    user: { publishAnalytikaEvent }
  } = useDispatch<AppDispatch>();
  const [customNearbyLocation, setCustomNearbyLocation] = useState<SearchLocation | undefined>();
  const [isDrivingDistanceFetching, setIsDrivingDistanceFetching] = useState(false);
  const [isFormError, setIsFormError] = useState(true);
  const [drivingDirections, setDrivingDirections] = useState<DrivingDistanceRoutesTransformed | null>(null);
  const [drivingDistance, setDrivingDistance] = useState<number | null>(null);
  const [isMapDraggable, setIsMapDraggable] = useState(false);
  const {
    merchantConfig: {
      isDropOffMandatory,
      isCustomFulfillmentDistanceActive,
      customFulfillmentDistance,
      standardFulfillmentDistance
    },
    userGalileoVars: { [FeatureFlag.IS_LOCATIONS_REVERSE_GEOCODING_DISABLED]: isReverseGeocodingDisabled }
  } = useSelector(userSelector);

  const userDefaultMapCenter = pickupLocation.location;

  useEffect(() => {
    form.setFieldValue('buildingName', usersBuildingName);
    if (initialDropoffLocation) {
      setCustomNearbyLocation(initialDropoffLocation);
      form.setFieldValue('dropOffId', initialDropoffLocation.id);
      setIsFormError(isDropOffMandatory && !usersBuildingName);
      return;
    }
    form.setFieldValue('dropOffId', preSelectedDropoffId);
    if (!preSelectedDropoffId || (isDropOffMandatory && !usersBuildingName)) {
      setIsFormError(true);
    } else {
      setIsFormError(false);
    }
  }, []);

  const handleFormChange = () => {
    const requiredFields = ['dropOffId'];
    isDropOffMandatory && requiredFields.push('buildingName');
    //The following check is added in order to get form state without pre-validation
    const isRequiredMissed = requiredFields.some(field => !form.getFieldValue(field));
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
    setIsFormError(hasErrors || isRequiredMissed);
  };

  const mutatedDropoffLocations = customNearbyLocation
    ? [customNearbyLocation, ...searchDrofOffLocations.filter(current => current.id !== customNearbyLocation?.id)]
    : searchDrofOffLocations;

  const mutatedDropoffSelectOptions = useMemo(
    () => getLocationSelectOptions(mutatedDropoffLocations, getDropOffLocationTitle),
    [mutatedDropoffLocations]
  );

  const dropoffIdSelectedModal = Form.useWatch('dropOffId', form);
  const dropOffLocationSelectedModal = useMemo(
    () =>
      dropoffIdSelectedModal
        ? mutatedDropoffLocations.find(location => location.id === dropoffIdSelectedModal)
        : undefined,
    [dropoffIdSelectedModal, customNearbyLocation]
  );

  const onClickBack = () => {
    publishAnalytikaEvent(customDropoffBackClicked());
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_BACK_CUSTOM_DROPOFF,
      event_version: 2,
      area: dropOffLocationSelectedModal?.area,
      street: dropOffLocationSelectedModal?.street,
      location_id: dropOffLocationSelectedModal?.id,
      place_id: dropOffLocationSelectedModal?.id,
      building_name: dropOffLocationSelectedModal?.building,
      dropoff_latitude: dropOffLocationSelectedModal?.location.lat,
      dropoff_longitude: dropOffLocationSelectedModal?.location.lng,
      dropoff_location: dropOffLocationSelectedModal?.formattedAddress || dropOffLocationSelectedModal?.name,
      estimated_distance_km: drivingDistance
    });
    setIsMapDraggable(false);
  };

  const onClickConfirm = () => {
    publishAnalytikaEvent(customDropoffConfirmClicked(dropOffLocationSelectedModal));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CONFIRM_CUSTOM_DROPOFF,
      event_version: 2,
      area: dropOffLocationSelectedModal?.area,
      street: dropOffLocationSelectedModal?.street,
      location_id: dropOffLocationSelectedModal?.id,
      place_id: dropOffLocationSelectedModal?.id,
      building_name: dropOffLocationSelectedModal?.building,
      dropoff_latitude: dropOffLocationSelectedModal?.location.lat,
      dropoff_longitude: dropOffLocationSelectedModal?.location.lng,
      dropoff_location: dropOffLocationSelectedModal?.formattedAddress || dropOffLocationSelectedModal?.name,
      estimated_distance_km: drivingDistance
    });
    setIsMapDraggable(false);
  };

  const onFinish = (fields: AddDropoffFormValues) => {
    onSubmitModal(fields, drivingDirections, dropOffLocationSelectedModal as SearchLocation);
    publishAnalytikaEvent(customDropoffSubmitClicked(dropOffLocationSelectedModal));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_SUBMIT_CUSTOM_DROPOFF,
      event_version: 2,
      area: dropOffLocationSelectedModal?.area,
      street: dropOffLocationSelectedModal?.street,
      location_id: dropOffLocationSelectedModal?.id,
      place_id: dropOffLocationSelectedModal?.id,
      building_name: dropOffLocationSelectedModal?.building,
      dropoff_latitude: dropOffLocationSelectedModal?.location.lat,
      dropoff_longitude: dropOffLocationSelectedModal?.location.lng,
      dropoff_location: dropOffLocationSelectedModal?.formattedAddress || dropOffLocationSelectedModal?.name,
      estimated_distance_km: drivingDistance
    });
    onClose();
  };

  const onCancelModal = () => {
    publishAnalytikaEvent(customDropoffCancelClicked());
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CANCEL_CUSTOM_DROPOFF,
      event_version: 2,
      area: dropOffLocationSelectedModal?.area,
      street: dropOffLocationSelectedModal?.street,
      location_id: dropOffLocationSelectedModal?.id,
      place_id: dropOffLocationSelectedModal?.id,
      building_name: dropOffLocationSelectedModal?.building,
      dropoff_latitude: dropOffLocationSelectedModal?.location.lat,
      dropoff_longitude: dropOffLocationSelectedModal?.location.lng,
      dropoff_location: dropOffLocationSelectedModal?.formattedAddress || dropOffLocationSelectedModal?.name,
      estimated_distance_km: drivingDistance
    });
    onClose();
  };

  const handleSetLocationOnMap = async () => {
    setIsMapDraggable(true);
    publishAnalytikaEvent(customDropoffSetLocationOnMapClicked());
    const startNearbyLocation = dropOffLocationSelectedModal
      ? dropOffLocationSelectedModal?.location
      : userDefaultMapCenter || DUBAI_LAT_LNG;
    await setCustomLocation(startNearbyLocation);
  };

  const setCustomLocation = async (dropoffLocationPoint: LocationPoint) => {
    //TODO: try & catch
    try {
      if (isReverseGeocodingDisabled) {
        reverseGeocodingBlocked();
        const e = new Error('Reverse geocoding is blocked for the user') as any;
        e.isReverseGeocodingBlocked = true;
        throw e;
      }

      const fetchedDropoff = await getReverseGeocode(dropoffLocationPoint);
      const customDropoff = Object.assign(fetchedDropoff, { location: dropoffLocationPoint });
      setCustomNearbyLocation(customDropoff);
      form.setFieldValue('dropOffId', customDropoff.id);
      form.validateFields(['dropOffId']);
    } catch (err) {
      if ((err as any).isReverseGeocodingBlocked) {
        // eslint-disable-next-line no-console
        console.error(err);
      } else {
        // eslint-disable-next-line no-console
        console.error('Error loading location details', err);
        noNearbyLocationFound();
        publishAnalytikaEvent(nearbyLocationNotFound(dropoffLocationPoint));
        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_NEARBY_LOC_ERR,
          event_version: 2,
          latitude: dropoffLocationPoint.lat,
          longitude: dropoffLocationPoint.lng
        });
      }
      const prevDropoff = form.getFieldValue('dropOffId');
      if (!prevDropoff) {
        form.setFieldValue('dropOffId', pickupLocation.id);
        // @ts-ignore
        setCustomNearbyLocation(pickupLocation);
        form.validateFields(['dropOffId']);
      }
    }
  };

  const onMarkerChange = async (dropoffLocationPoint: LocationPoint) => {
    publishAnalytikaEvent(
      dropofPinDragged(analyticsScreen, {
        dropoff_lat: dropoffLocationPoint.lat,
        dropoff_long: dropoffLocationPoint.lng
      })
    );
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_DRAG_DROPOFF_PIN,
      event_version: 2,
      dropoff_latitude: dropoffLocationPoint.lat,
      dropoff_longitude: dropoffLocationPoint.lng
    });
    await setCustomLocation(dropoffLocationPoint);
  };

  const onDropoffSelectChange = (id: string) => {
    publishAnalytikaEvent(dropOffLocationChanged({ drop_off_location_id: id }));
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_UPDATE_DROPOFF,
      event_version: 2,
      dropoff_latitude: form.getFieldValue('lat'),
      dropoff_longitude: form.getFieldValue('lng'),
      building_name: form.getFieldValue('buildingName')
    });
    // setCustomLatLng(undefined);
    setCustomNearbyLocation(undefined);
  };

  const dropoffValidator = async (rule: RuleObject, locationId: string) => {
    if (!locationId) {
      publishAnalytikaEvent(emptyDropOffError());
      return Promise.reject(t('deliveries.dropoffLocation.required'));
    }
    const newDropOffLocation = mutatedDropoffLocations.find(dropoffItem => locationId === dropoffItem.id);

    if (isEqual(pickupLocation?.location, newDropOffLocation?.location)) {
      publishAnalytikaEvent(samePickupDropOffError());
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_SAME_PICK_DROP,
        event_version: 2,
        dropoff_latitude: newDropOffLocation?.location?.lat,
        dropoff_longitude: newDropOffLocation?.location?.lng,
        dropoff_location: newDropOffLocation?.formattedAddress || newDropOffLocation?.name,
        pickup_latitude: pickupLocation?.location.lat,
        pickup_longitude: pickupLocation?.location.lng,
        pick_up_location: pickupLocation?.building || pickupLocation.name
      });
      return Promise.reject(t('deliveries.dropoffLocation.sameAsPickUp'));
    }
    try {
      const fulfillmentDistance = isCustomFulfillmentDistanceActive
        ? customFulfillmentDistance
        : standardFulfillmentDistance;

      setIsDrivingDistanceFetching(true);

      const { distance, directions } = await getDistance({
        pickupLocation: pickupLocation.location,
        dropOffLocation: newDropOffLocation!.location
      });

      setIsDrivingDistanceFetching(false);
      setDrivingDirections(directions);
      setDrivingDistance(distance);

      if (!isValidDistanceV2(fulfillmentDistance!, distance)) {
        publishAnalytikaEvent(
          ringfencingError({
            calculated_distance: distance,
            dropoff_location_id: newDropOffLocation?.id,
            dropoff_lat_lng: newDropOffLocation?.location,
            dropoff_location_name: newDropOffLocation?.formattedAddress || newDropOffLocation?.name,
            pickup_location_id: pickupLocation?.id,
            pickup_location_name: pickupLocation?.building || pickupLocation?.name,
            pickup_lat_lng: pickupLocation?.location
          })
        );

        publishAV2Event({
          event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_RINGFENCE_ORD,
          event_version: 2,
          calculated_distance: distance,
          drop_off_location: newDropOffLocation?.formattedAddress || newDropOffLocation?.name,
          dropoff_latitude: newDropOffLocation?.location.lat,
          dropoff_longitude: newDropOffLocation?.location.lng,
          pickup_location_name: pickupLocation?.building || pickupLocation?.name,
          pickup_latitude: pickupLocation?.location.lat,
          pickup_longitude: pickupLocation?.location.lng
        });

        return Promise.reject(t('deliveries.dropoffLocation.tooFarWithDistance', { distance: fulfillmentDistance }));
      } else {
        return Promise.resolve();
      }
    } catch (err) {
      setIsDrivingDistanceFetching(false);
      setDrivingDirections(null);
      setDrivingDistance(null);
      return Promise.reject('network error');
    }
  };

  const isSubmitDisabled = isFormError || isDropoffSearchLoading || isDrivingDistanceFetching;

  return (
    <Modal
      width={820}
      title={
        <Typography.Title level={2}>
          {t(isEditMode ? 'modal.addDropOff.editTitle' : 'modal.addDropOff.title')}
        </Typography.Title>
      }
      className={styles.modalContent}
      visible={visible}
      onClose={onCancelModal}
      data-testid="modalAddDropOff"
      footer={
        <Row gutter={1} justify="end">
          <Col md={12} lg={12} />
          {isMapDraggable ? (
            <Space>
              <Col className={styles.footerCol} md={3} lg={3}>
                <Button size={'large'} onClick={onClickBack} data-testid="backButton">
                  {t('modal.addDropOff.back')}
                </Button>
              </Col>
              <Col className={styles.footerCol} md={3} lg={3}>
                <Button size={'large'} type="primary" onClick={onClickConfirm} data-testid="confirmButton">
                  {t('modal.addDropOff.confirm')}
                </Button>
              </Col>
            </Space>
          ) : (
            <Space>
              <Col className={styles.footerCol} md={6} lg={4}>
                <Button data-testid="cancelDropoffButton" size={'large'} onClick={onCancelModal}>
                  {t('modal.addDropOff.cancel')}
                </Button>
              </Col>
              <Col className={styles.footerCol} md={6} lg={4}>
                <Button
                  size={'large'}
                  type="primary"
                  disabled={isSubmitDisabled}
                  onClick={() => form.submit()}
                  data-testid="submitDropoffButton"
                >
                  {t(isEditMode ? 'modal.addDropOff.save' : 'modal.addDropOff.continue')}
                </Button>
              </Col>
            </Space>
          )}
        </Row>
      }
    >
      <Form onFinish={onFinish} onFieldsChange={handleFormChange} form={form} layout="vertical">
        <Row>
          <Col span={24}>
            <Form.Item
              hidden={isMapDraggable}
              name="dropOffId"
              label={t('deliveries.dropoffLocation.label')}
              rules={[{ required: true, validator: dropoffValidator }]}
            >
              <Select
                getPopupContainer={trigger => trigger.parentElement}
                size={'large'}
                showSearch
                defaultActiveFirstOption={false}
                placeholder={t('deliveries.dropoffLocation.placeholder')}
                notFoundContent={isDropoffSearchLoading ? <Spin /> : t('deliveries.dropoffLocation.missing')}
                optionFilterProp="children"
                filterOption={false}
                onSearch={onDropoffSearchStart}
                loading={isDrivingDistanceFetching}
                data-testid="dropOffLocationModalSelect"
                onChange={onDropoffSelectChange}
              >
                {mutatedDropoffSelectOptions}
              </Select>
            </Form.Item>
            <div className={classnames([styles.mapContainer, isMapDraggable && styles.large])}>
              <Map
                mapUseCase={MapUseCase.SelectDropoff}
                useExternalDirections
                dropOff={dropOffLocationSelectedModal}
                onDropOffMarkerChange={isMapDraggable && onMarkerChange}
                containerClassName={classnames([styles.mapContainer, isMapDraggable && styles.large])}
                overlay={!dropoffIdSelectedModal ? overlay : ''}
                mapDefaultCenter={userDefaultMapCenter}
                mapBar={
                  <InfoBar
                    isButtonVisible={!isMapDraggable}
                    onButtonClick={handleSetLocationOnMap}
                    title={customNearbyLocation?.building}
                    text={customNearbyLocation?.city}
                  />
                }
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              hidden={isMapDraggable}
              name="buildingName"
              label={t('deliveries.buildingName.label')}
              rules={[{ required: isDropOffMandatory, message: t('deliveries.buildingName.required') }]}
            >
              <Input
                maxLength={200}
                size={'large'}
                placeholder={t('deliveries.buildingName.placeholder')}
                data-testid="buildingName"
                autoComplete="off"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddDropoffModal;
